import { TemplateFieldType, type TemplateField } from '@/api';
import type { TemplateFieldMetadataMap } from '@/types/contractGeneration/wizard';
import { orderSiblings } from './templateFieldLabels';

/**
 * One row of a template schema flattened for display: nested object/list
 * fields become indented rows (`depth`) in pre-order, so a plain v-for can
 * render the tree.
 */
export interface FlattenedField {
  key: string;
  /**
   * Key with the entity-type prefix stripped for display: in the document an
   * entity role renders as `natural_person_friend`, but users know it as the
   * role `friend` (the type is conveyed separately). Equals `key` for
   * non-entity fields.
   */
  displayKey: string;
  path: string;
  depth: number;
  hasChildren: boolean;
  type: TemplateFieldType | 'unknown';
  required: boolean;
  defaultValue: string | null;
  selectionOptions: string[];
  filters: string[];
  isCollection: 'object' | 'list' | null;
  itemType?: TemplateFieldType | null;
}

export const FIELD_TYPE_LABEL: Record<string, string> = {
  string: 'Text',
  number: 'Number',
  date: 'Date',
  boolean: 'Yes / No',
  selection: 'Selection',
  object: 'Object',
  list: 'List',
  legal_entity: 'Legal entity',
  natural_person: 'Natural person',
  unknown: 'Unknown',
};

export const FIELD_TYPE_TAG: Record<
  string,
  'default' | 'primary' | 'info' | 'success' | 'warning' | 'error'
> = {
  string: 'info',
  number: 'info',
  date: 'info',
  boolean: 'default',
  selection: 'success',
  object: 'warning',
  list: 'warning',
  legal_entity: 'primary',
  natural_person: 'primary',
  unknown: 'default',
};

function formatFilter(filter: { name: string; args?: string[] }): string {
  if (!filter.args || filter.args.length === 0) return filter.name;
  return `${filter.name}(${filter.args.join(', ')})`;
}

/**
 * Recover the user-facing alias from an entity schema key. The backend names
 * entity context variables `<entity_type>_<alias>` (singular) and
 * `<entity_type plural>_<alias>` (entity lists) so the extractor can infer
 * the type from the name — mirrors `tag_discovery`'s prefix stripping.
 * Returns the key unchanged for everything else.
 */
export function entityAliasFromSchemaKey(key: string, field: TemplateField): string {
  if (field.type === TemplateFieldType.LEGAL_ENTITY && key.startsWith('legal_entity_')) {
    return key.slice('legal_entity_'.length);
  }
  if (field.type === TemplateFieldType.NATURAL_PERSON && key.startsWith('natural_person_')) {
    return key.slice('natural_person_'.length);
  }
  if (field.type === TemplateFieldType.LIST) {
    if (field.item_type === TemplateFieldType.LEGAL_ENTITY && key.startsWith('legal_entities_')) {
      return key.slice('legal_entities_'.length);
    }
    if (
      field.item_type === TemplateFieldType.NATURAL_PERSON &&
      key.startsWith('natural_persons_')
    ) {
      return key.slice('natural_persons_'.length);
    }
  }
  return key;
}

export function flattenFields(
  schemaFields: { [key: string]: TemplateField } | undefined | null,
  parentPath: string = '',
  depth: number = 0,
  output: FlattenedField[] = [],
  /** Authored field order: siblings follow it when given, document order otherwise. */
  metadata: TemplateFieldMetadataMap | null = null
): FlattenedField[] {
  if (!schemaFields) return output;
  for (const [key, field] of orderSiblings(Object.entries(schemaFields), parentPath, metadata)) {
    const path = parentPath ? `${parentPath}.${key}` : key;
    const hasChildren = Boolean(
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- boolean OR, not a nullish fallback
      (field.fields && Object.keys(field.fields).length > 0) ||
      (field.item_fields && Object.keys(field.item_fields).length > 0)
    );
    const flat: FlattenedField = {
      key,
      displayKey: entityAliasFromSchemaKey(key, field),
      path,
      depth,
      hasChildren,
      type: field.type ?? 'unknown',
      required: Boolean(field.required),
      defaultValue: field.default_value ?? null,
      selectionOptions: field.selection_options ?? [],
      filters: (field.filters ?? []).map(formatFilter),
      isCollection: field.type === TemplateFieldType.LIST ? 'list' : field.fields ? 'object' : null,
      itemType: field.item_type ?? null,
    };
    output.push(flat);
    if (field.fields) {
      flattenFields(field.fields, path, depth + 1, output, metadata);
    }
    if (field.item_fields) {
      flattenFields(field.item_fields, `${path}[]`, depth + 1, output, metadata);
    }
  }
  return output;
}
